import React, { useId } from "react";
import { motion, useReducedMotion } from "framer-motion";

// Vector trace of the Applumo blocks icon (source artwork is 1254px square).
const BLOCKS_PATH =
  "M326 438 H449 Q497 438 497 486 V590 C497 615 517 632 542 632 C565 632 578 615 595 597 C610 582 625 575 645 575 H683 Q731 575 731 623 V713 C731 737 750 757 775 757 H865 Q913 757 913 805 V910 Q913 958 865 958 H731 Q683 958 683 910 V815 C683 790 663 770 638 770 H566 Q518 770 518 722 V700 C518 672 495 650 467 650 H326 Q278 650 278 602 V486 Q278 438 326 438 Z";

const LIGHT_PIECES = [
  {
    type: "path",
    d: "M590 340 H727 Q775 340 775 388 V505 C775 540 795 568 830 568 H898 Q943 568 943 613 V687 Q943 732 898 732 H810 Q765 732 765 687 V610 C765 575 740 550 705 550 H590 Q540 550 540 500 V390 Q540 340 590 340 Z",
  },
  { type: "circle", cx: 892, cy: 450, r: 84 },
  { type: "rect", x: 272, y: 684, width: 225, height: 219, rx: 48 },
];

const EASE = [0.22, 1, 0.36, 1];

/**
 * variant: "color" (ink blocks + rose light), "onDark" (white blocks + rose light),
 *          "mono" (white blocks + ink light, for rose backgrounds)
 */
const BrandMark = ({ variant = "color", animated = false, className = "", title }) => {
  const gradientId = useId().replace(/:/g, "");
  const reduceMotion = useReducedMotion();
  const play = animated && !reduceMotion;

  const blockFill = variant === "color" ? "#0a0a0a" : "#ffffff";
  const lightFill = variant === "mono" ? "#0a0a0a" : `url(#${gradientId})`;

  const piece = (p, i) => {
    const common = {
      fill: lightFill,
      initial: play ? { opacity: 0, scale: 0.6 } : false,
      animate: play ? { opacity: 1, scale: 1 } : undefined,
      transition: { delay: 0.35 + i * 0.12, duration: 0.7, ease: EASE },
      style: { transformBox: "fill-box", transformOrigin: "center" },
    };
    if (p.type === "circle") {
      return (
        <motion.circle
          key={i}
          {...common}
          cx={p.cx}
          cy={p.cy}
          r={p.r}
          animate={play ? { opacity: 1, scale: [1, 1.06, 1] } : undefined}
          transition={
            play
              ? { opacity: { delay: 0.6, duration: 0.6 }, scale: { delay: 1.3, duration: 3.2, repeat: Infinity, ease: "easeInOut" } }
              : undefined
          }
        />
      );
    }
    if (p.type === "rect") return <motion.rect key={i} {...common} x={p.x} y={p.y} width={p.width} height={p.height} rx={p.rx} />;
    return <motion.path key={i} {...common} d={p.d} />;
  };

  return (
    <svg
      viewBox="262 330 724 638"
      className={className}
      role={title ? "img" : undefined}
      aria-hidden={title ? undefined : true}
      aria-label={title}
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#f43f5e" />
          <stop offset="1" stopColor="#e11d48" />
        </linearGradient>
      </defs>
      <motion.path
        d={BLOCKS_PATH}
        fill={blockFill}
        initial={play ? { opacity: 0, y: 30 } : false}
        animate={play ? { opacity: 1, y: 0 } : undefined}
        transition={{ duration: 0.8, ease: EASE }}
      />
      {LIGHT_PIECES.map(piece)}
    </svg>
  );
};

export const Wordmark = ({ variant = "color", className = "" }) => (
  <span className={`inline-flex items-center gap-2 sm:gap-2.5 ${className}`} dir="ltr">
    <BrandMark variant={variant} className="h-6 sm:h-7 w-auto" />
    <span
      className={`text-[19px] sm:text-[22px] font-extrabold tracking-[-0.03em] ${
        variant === "color" ? "text-ink" : "text-white"
      }`}
    >
      applumo
    </span>
  </span>
);

export default BrandMark;
